/**
 * Holding page configuration
 * ----------------------------
 * This is the only file you need to edit for copy, legal details, and the
 * newsletter form. The HTML does not contain business data.
 *
 * Languages live under `locales`. Shared identity (brand, email, KvK) stays
 * at the top level. Add or edit EN / NL / JA / ZH copy in the matching block;
 * the switcher is built from whatever keys exist here.
 *
 * View a language: ?lang=en | ?lang=nl | ?lang=ja | ?lang=zh
 * The switcher also remembers the last choice in the browser.
 *
 * Exported as the `siteConfig` object (attached to `window` so the page
 * also works when opened as a local file, where ES modules are blocked).
 */

const siteConfig = {
  // Primary wordmark. Rendered in lowercase with wide letter-spacing.
  brandName: "Gashuu",

  // Optional secondary mark (e.g. a registered kanji name). Leave empty
  // if unused. This is identity, not decoration, do not add a mark for
  // atmosphere.
  brandNameSecondary: "",

  // Optional catalog index in the masthead (e.g. "00" or "Vol. 0").
  edition: "00",

  // Public contact, stored reversed and base64 encoded so the address is
  // not sitting in a fetchable file for harvesters to regex out. site.js
  // decodes it only when a visitor clicks. Generate a new one with:
  //   node -e "console.log(Buffer.from([...'you@example.com'].reverse().join('')).toString('base64'))"
  contactEmailPacked: "ZXJvdHMudXVoc2FnQG9mbmk=",

  // Fallback if ?lang= is missing and the browser language is not listed.
  defaultLocale: "en",

  // Plainly visible in the footer. Fill these before sending trade emails,
  // a distributor should be able to check KvK in a few seconds.
  // Country *name* is translated per locale; the number and address stay as registered.
  legal: {
    companyName: "", // e.g. "Gashuu B.V."
    kvkNumber: "42144439", // 8-digit Kamer van Koophandel number
    vatNumber: "", // e.g. "NL123456789B01"
    address: "Ede", // registered address, one line: "Street 1, 1234 AB City"
  },

  newsletter: {
    // Set to false to hide the form entirely.
    enabled: true,

    // Formspree:  https://formspree.io/f/xxxxxxxx
    // MailerLite: https://assets.mailerlite.com/jsonp/{id}/forms/{id}/subscribe
    // Leave empty to hide the form until an endpoint is ready.
    formAction: "",

    // Form field name for the email input.
    // Formspree expects "email". MailerLite embedded forms often use "fields[email]".
    emailFieldName: "email",
  },

  // Footer links. Empty array hides the list. Example:
  // { label: "Instagram", url: "https://instagram.com/…" }
  social: [],

  locales: {
    en: {
      htmlLang: "en",
      switchLabel: "EN",
      tagline: "Asian art books, in print.",
      intro:
        "A specialised shop for Asian pop culture art books: artist publications, exhibition catalogues, and in-print editions chosen for the work on the page.\n\nWe empower the next generation of overseas (digital) artists, mangakas, illustrators, and creators with authentic reference materials.",
      launchNote: "Opening late 2026",
      country: "The Netherlands",
      newsletter: {
        placeholder: "Email address",
        buttonLabel: "Notify me",
        successMessage: "Thank you. We will write when we open.",
      },
      ui: {
        language: "Language",
        list: "List",
        mail: "Mail",
        showEmail: "Show email address",
        company: "Company",
        kvk: "Chamber of Commerce (KvK)",
        vat: "VAT",
        address: "Address",
        country: "Country",
        emailField: "Email address",
        invalidEmail: "Please enter a valid email address.",
        sending: "Sending…",
        error: "Something went wrong. Please try again, or email us.",
      },
    },

    nl: {
      htmlLang: "nl",
      switchLabel: "NL",
      tagline: "Aziatische kunstboeken, in druk.",
      intro:
        "Een gespecialiseerde winkel in kunstboeken over Aziatische popcultuur: kunstenaarsuitgaven, tentoonstellingscatalogi en nog leverbare edities, gekozen om het werk op de pagina.\n\nWe helpen de volgende generatie kunstenaars, mangaka's, illustratoren en makers in het buitenland, digitaal of niet, aan authentiek referentiemateriaal.",
      launchNote: "Opening eind 2026",
      country: "Nederland",
      newsletter: {
        placeholder: "E-mailadres",
        buttonLabel: "Aanmelden",
        successMessage: "Dank u. We schrijven u wanneer we openen.",
      },
      ui: {
        language: "Taal",
        list: "Lijst",
        mail: "Mail",
        showEmail: "E-mailadres tonen",
        company: "Bedrijf",
        kvk: "KvK",
        vat: "btw",
        address: "Adres",
        country: "Land",
        emailField: "E-mailadres",
        invalidEmail: "Vul een geldig e-mailadres in.",
        sending: "Verzenden…",
        error: "Er ging iets mis. Probeer het opnieuw, of mail ons.",
      },
    },

    ja: {
      htmlLang: "ja",
      switchLabel: "日本語",
      tagline: "アジアのアートブックを、印刷で。",
      intro:
        "アジアのポップカルチャーに特化したアートブック店です。アーティストブック、展覧会カタログ、そして紙面の仕事で選んだ現行の刊本を扱います。\n\n海外で活動する次世代の (デジタル) アーティスト、漫画家、イラストレーター、作り手に、本物の参考資料を届けます。",
      launchNote: "2026年後半 開店",
      country: "オランダ",
      newsletter: {
        placeholder: "メールアドレス",
        buttonLabel: "登録する",
        successMessage: "ありがとうございます。開店の際にご連絡します。",
      },
      ui: {
        language: "言語",
        list: "案内",
        mail: "メール",
        showEmail: "メールアドレスを表示",
        company: "会社",
        kvk: "KvK",
        vat: "VAT",
        address: "所在地",
        country: "国",
        emailField: "メールアドレス",
        invalidEmail: "有効なメールアドレスを入力してください。",
        sending: "送信中…",
        error: "送信できませんでした。もう一度試すか、メールでご連絡ください。",
      },
    },

    zh: {
      htmlLang: "zh-Hans",
      switchLabel: "中文",
      tagline: "亚洲美术书，纸本在售。",
      intro:
        "一家专做亚洲流行文化美术书的小店：艺术家自出版物、展览图录，以及仍在印的版本，按内页的作品来挑。\n\n我们把真正可用的参考资料，交到海外新一代（数字）艺术家、漫画家、插画师和创作者手里。",
      launchNote: "2026 年下半年开业",
      country: "荷兰",
      newsletter: {
        placeholder: "电子邮箱",
        buttonLabel: "通知我",
        successMessage: "谢谢。开业时我们会写信给你。",
      },
      ui: {
        language: "语言",
        list: "订阅",
        mail: "邮件",
        showEmail: "显示电子邮箱",
        company: "公司",
        kvk: "荷兰商会登记号 (KvK)",
        vat: "增值税号",
        address: "地址",
        country: "国家",
        emailField: "电子邮箱",
        invalidEmail: "请输入有效的电子邮箱。",
        sending: "发送中…",
        error: "出了点问题。请重试，或给我们发邮件。",
      },
    },
  },
};

window.siteConfig = siteConfig;
